import type { SyntheticEvent } from "react";
import { Link } from "react-router-dom";
import { Chip, Stack, Typography } from "@mui/material";
import FieldRow from "@/components/mobile/FieldRow";
import MobileCard from "@/components/mobile/MobileCard";
import { formatMoney } from "@/pages/InvoiceManagement/utils/money";
import { V2_COLORS } from "@/pages/InvoiceManagement/v2/constants/colors";
import type { VendorRfqSummary } from "../types";
import { budgetLabel } from "../utils/budget";
import { formatCalendarDate, formatRespondBy } from "../utils/deadline";
import { submissionOf, tradesOf } from "../utils/response";
import { describeRfq, projectLine } from "../utils/rfqCardCopy";
import RfqStatusBadge from "./RfqStatusBadge";

interface MobileRfqCardProps {
  rfq: VendorRfqSummary;
  now: number;
  onOpen: (rfq: VendorRfqSummary) => void;
}

const linkStyle = { color: V2_COLORS.primary, fontWeight: 600, textDecoration: "none", fontSize: "13px" };

export default function MobileRfqCard({ rfq, now, onOpen }: MobileRfqCardProps) {
  const copy = describeRfq(rfq, now);
  const submission = submissionOf(rfq);
  const budget = budgetLabel(rfq.project_budget);
  const keepCardClosed = (event: SyntheticEvent) => event.stopPropagation();

  return (
    <MobileCard onClick={() => onOpen(rfq)}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
        <Typography fontSize="14px" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
          {rfq.rfq_number}
        </Typography>
        <RfqStatusBadge status={rfq.status} />
        {copy.mode && <Chip size="small" label={copy.mode} variant="outlined" />}
      </Stack>
      <Typography fontSize="13px" sx={{ color: V2_COLORS.textSecondary, mb: 0.5 }}>
        {projectLine(rfq)}
      </Typography>
      {copy.countdown && (
        <FieldRow
          label="Respond by"
          value={
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <span>{formatRespondBy(rfq.respond_by)}</span>
              <Chip
                size="small"
                label={copy.countdown}
                sx={{
                  fontWeight: 600,
                  color: copy.windowOpen ? V2_COLORS.warning : V2_COLORS.textSecondary,
                  backgroundColor: copy.windowOpen ? V2_COLORS.warningBg : V2_COLORS.bgMuted,
                }}
              />
            </Stack>
          }
        />
      )}
      <FieldRow label="Ask total" value={formatMoney(rfq.ask_total)} />
      {submission && <FieldRow label="Your total" value={formatMoney(submission.bid_total)} />}
      {budget && <FieldRow label="Project budget" value={budget} />}
      <FieldRow label="Expected delivery" value={formatCalendarDate(rfq.expected_delivery_date)} />
      <FieldRow label="Site pincode" value={rfq.site_pincode} />
      <FieldRow label="Lines" value={String(rfq.line_count)} />
      <FieldRow label="Trades" value={tradesOf(rfq)} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mt: 1 }}>
        <Typography fontSize="12px" fontWeight={500}>
          {copy.headline}
        </Typography>
        <Link to={copy.action.to} style={linkStyle} onClick={keepCardClosed} onKeyDown={keepCardClosed}>
          {copy.action.label}
        </Link>
      </Stack>
    </MobileCard>
  );
}
